import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Chip, IconButton, Tooltip, LinearProgress } from "@mui/material";
import { MaterialReactTable } from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import BarChartIcon from "@mui/icons-material/BarChart";
import { setModal, cleanIsUpdate } from "../../features/modal/ModalSlice";
import CustomButton from "../../components/Button/CustomButton";
import apiClient from "../../services/apiClient";
import toast from "react-hot-toast";

const OutboundCalls = () => {
  const dispatch = useDispatch();
  const modal = useSelector((state) => state.modal?.modal);

  // ========== COMMENTED OLD HARDCODED DATA ==========
  // const [campaigns, setCampaigns] = useState([
  //   {
  //     id: 1,
  //     name: "New Year Promotion",
  //     assistant: "Sales Agent",
  //     contacts: 1500,
  //     completed: 1245,
  //     successful: 987,
  //     failed: 258,
  //     status: "Active",
  //     startDate: "2024-01-10",
  //     endDate: "2024-01-20",
  //     callsPerHour: 100,
  //   },
  //   {
  //     id: 2,
  //     name: "Customer Satisfaction Survey",
  //     assistant: "Survey Bot",
  //     contacts: 850,
  //     completed: 850,
  //     successful: 756,
  //     failed: 94,
  //     status: "Completed",
  //     startDate: "2024-01-05",
  //     endDate: "2024-01-12",
  //     callsPerHour: 75,
  //   },
  //   {
  //     id: 3,
  //     name: "Appointment Reminders",
  //     assistant: "Appointment Scheduler",
  //     contacts: 2300,
  //     completed: 1567,
  //     successful: 1423,
  //     failed: 144,
  //     status: "Active",
  //     startDate: "2024-01-08",
  //     endDate: "2024-01-25",
  //     callsPerHour: 120,
  //   },
  //   {
  //     id: 4,
  //     name: "Payment Collection",
  //     assistant: "Collection Agent",
  //     contacts: 450,
  //     completed: 0,
  //     successful: 0,
  //     failed: 0,
  //     status: "Scheduled",
  //     startDate: "2024-01-18",
  //     endDate: "2024-01-22",
  //     callsPerHour: 50,
  //   },
  //   {
  //     id: 5,
  //     name: "Product Launch Announcement",
  //     assistant: "Marketing Agent",
  //     contacts: 3200,
  //     completed: 875,
  //     successful: 654,
  //     failed: 221,
  //     status: "Paused",
  //     startDate: "2024-01-12",
  //     endDate: "2024-01-30",
  //     callsPerHour: 150,
  //   },
  // ]);

  // ========== NEW DYNAMIC STATE ==========
  const [campaigns, setCampaigns] = useState([]);

  const [loading, setLoading] = useState(false);

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  const [paginationInfo, setPaginationInfo] = useState({
    total_count: 0,
    total_pages: 0,
  });

  // ========== FETCH CAMPAIGNS ON MOUNT ==========
  useEffect(() => {
    fetchCampaigns();
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    if (modal?.isUpdated?.isUpdatedIn === "OUTBOUND_CALLS") {
      fetchCampaigns();
      // Clean up the update flag to prevent multiple triggers
      dispatch(cleanIsUpdate());
    }
  }, [modal?.isUpdated?.isUpdatedIn]);

  // ========== API INTEGRATION ==========
  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get("/campaigns", {
        page: pagination.pageIndex + 1,
        page_size: pagination.pageSize,
      });

      if (response && response.status && response.data) {
        // Map API response to UI format
        const mappedCampaigns = response.data.map((campaign) => ({
          id: campaign._id,
          name: campaign.campaign_name,
          assistant: campaign.campaign_type_details?.type || campaign.campaignType_description || "N/A",
          contacts: campaign.total_contacts || 0,
          completed: 0, // Not provided in API
          successful: 0, // Not provided in API
          failed: 0, // Not provided in API
          status: campaign.schedule_type === "Start Immediately" ? "Active" : "Scheduled",
          startDate: new Date().toISOString().split('T')[0], // Default to today
          endDate: "", // Not provided in API
          callsPerHour: campaign.call_rate || 0,
          // Keep original data for reference
          _original: campaign,
        }));

        setCampaigns(mappedCampaigns);
        setPaginationInfo({
          total_count: response.pagination?.total_count || 0,
          total_pages: response.pagination?.total_pages || 0,
        });
      } else {
        setCampaigns([]);
      }

      setLoading(false);
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      toast.error("Failed to fetch campaigns");
      setCampaigns([]);
      setLoading(false);
    }
  };

  const handleCreateCampaign = () => {
    dispatch(setModal({ type: "CREATE_OUTBOUND_CAMPAIGN" }));
  };

  const handleEditCampaign = (campaign) => {
    dispatch(setModal({ type: "EDIT_OUTBOUND_CAMPAIGN", data: campaign }));
  };

  const handleDeleteCampaign = async (campaign) => {
    if (window.confirm(`Are you sure you want to delete campaign "${campaign.name}"? This action cannot be undone.`)) {
      try {
        await apiClient.delete(`/campaigns/${campaign.id}`);
        toast.success("Campaign deleted successfully");
        // Refresh the campaigns list
        fetchCampaigns();
      } catch (error) {
        console.error("Error deleting campaign:", error);
        toast.error("Failed to delete campaign");
      }
    }
  };

  const handleToggleCampaign = (campaign) => {
    const newStatus = campaign.status === "Active" ? "Paused" : "Active";
    setCampaigns(
      campaigns.map((c) =>
        c.id === campaign.id ? { ...c, status: newStatus } : c
      )
    );
  };

  const handleViewAnalytics = (campaign) => {
    dispatch(setModal({ type: "VIEW_CAMPAIGN_ANALYTICS", data: campaign }));
  };

  const getStatusColor = (status) => {
    const colors = {
      Active: { bg: "#E8F5E9", color: "#2E7D32" },
      Paused: { bg: "#FFF3E0", color: "#E65100" },
      Completed: { bg: "#E3F2FD", color: "#1565C0" },
      Scheduled: { bg: "#F3E5F5", color: "#7B1FA2" },
    };
    return colors[status] || { bg: "#F5F5F5", color: "#666" };
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Campaign Name",
        size: 200,
        Cell: ({ row }) => (
          <Box>
            <Typography variant="body2" fontWeight={600}>
              {row.original.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.original.assistant}
            </Typography>
          </Box>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        size: 120,
        Cell: ({ cell }) => {
          const colors = getStatusColor(cell.getValue());
          return (
            <Chip
              label={cell.getValue()}
              size="small"
              sx={{
                backgroundColor: colors.bg,
                color: colors.color,
                fontWeight: 600,
              }}
            />
          );
        },
      },
      {
        accessorKey: "progress",
        header: "Progress",
        size: 180,
        Cell: ({ row }) => {
          const progress = (row.original.completed / row.original.contacts) * 100;
          return (
            <Box>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                <Typography variant="caption">
                  {row.original.completed} / {row.original.contacts}
                </Typography>
                <Typography variant="caption" fontWeight={600}>
                  {progress.toFixed(0)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#E0E0E0",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: progress >= 90 ? "#4CAF50" : "#2196F3",
                  },
                }}
              />
            </Box>
          );
        },
      },
      {
        accessorKey: "successful",
        header: "Success Rate",
        size: 120,
        Cell: ({ row }) => {
          const rate =
            row.original.completed > 0
              ? (row.original.successful / row.original.completed) * 100
              : 0;
          return (
            <Typography
              variant="body2"
              fontWeight={600}
              color={rate >= 80 ? "success.main" : rate >= 60 ? "warning.main" : "error.main"}
            >
              {rate.toFixed(1)}%
            </Typography>
          );
        },
      },
      {
        accessorKey: "callsPerHour",
        header: "Pacing",
        size: 100,
        Cell: ({ cell }) => (
          <Typography variant="body2" color="text.secondary">
            {cell.getValue()}/hr
          </Typography>
        ),
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        size: 110,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        size: 110,
        Cell: ({ cell }) => (
          <Typography variant="body2" sx={{ fontSize: 12 }}>
            {cell.getValue()}
          </Typography>
        ),
      },
    ],
    []
  );

  const totalCampaigns = paginationInfo.total_count;
  const activeCampaigns = campaigns.filter((c) => c.status === "Active").length;
  const totalContacts = campaigns.reduce((sum, c) => sum + c.contacts, 0);
  const totalCompleted = campaigns.reduce((sum, c) => sum + c.completed, 0);
  const completionRate = totalContacts > 0 ? (totalCompleted / totalContacts) * 100 : 0;

  return (
    <Box>
      {/* Header Section - White Background */}
      <Box sx={{
        backgroundColor: "#FFFFFF",
        borderBottom: "1px solid #E5E7EB",
        px: 4,
        py: 3
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              Outbound Call Campaigns
            </Typography>
            <Typography variant="body2" color="#6B7280">
              Create and manage automated outbound calling campaigns
            </Typography>
          </Box>
          <CustomButton
            icon={<AddIcon />}
            iconPosition="left"
            onClick={handleCreateCampaign}
            text="Create Campaign"
          />
        </Box>
      </Box>

      {/* Stats Cards Section - Separate */}
      <Box sx={{ px: 4, pt: 3, pb: 2 }}>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#111827" sx={{ mb: 0.5 }}>
              {totalCampaigns}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Campaigns
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#10B981" sx={{ mb: 0.5 }}>
              {activeCampaigns}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Active Campaigns
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#3B82F6" sx={{ mb: 0.5 }}>
              {totalContacts.toLocaleString()}
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Total Contacts
            </Typography>
          </Box>
          <Box sx={{
            flex: 1,
            px: 3,
            py: 2.5,
            borderRadius: 2,
            backgroundColor: "#FFFFFF",
            border: "1px solid #E5E7EB",
            transition: "all 0.2s",
            "&:hover": {
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }
          }}>
            <Typography variant="h5" fontWeight={600} color="#F59E0B" sx={{ mb: 0.5 }}>
              {completionRate.toFixed(0)}%
            </Typography>
            <Typography variant="caption" color="#6B7280" sx={{ fontSize: "0.75rem" }}>
              Overall Completion
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Table Section */}
      <Box sx={{ px: 4, pb: 3 }}>
        <Box sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: 2,
          border: "1px solid #E5E7EB",
          overflow: "hidden",
          p: 2
        }}>
          <MaterialReactTable
            columns={columns}
            data={campaigns}
            manualPagination
            rowCount={paginationInfo.total_count}
            onPaginationChange={setPagination}
            state={{
              isLoading: loading,
              pagination: pagination,
            }}
            enableRowActions
            positionActionsColumn="last"
            renderRowActions={({ row }) => (
              <Box sx={{ display: "flex", gap: 0.5 }}>
                {(row.original.status === "Active" || row.original.status === "Paused") && (
                  <Tooltip title={row.original.status === "Active" ? "Pause" : "Resume"}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleCampaign(row.original)}
                      sx={{
                        color: row.original.status === "Active" ? "warning.main" : "success.main",
                      }}
                    >
                      {row.original.status === "Active" ? <PauseIcon /> : <PlayArrowIcon />}
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="View Analytics">
                  <IconButton
                    size="small"
                    onClick={() => handleViewAnalytics(row.original)}
                    sx={{ color: "info.main" }}
                  >
                    <BarChartIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit">
                  <IconButton
                    size="small"
                    onClick={() => handleEditCampaign(row.original)}
                    sx={{ color: "primary.main" }}
                  >
                    <EditIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteCampaign(row.original)}
                    sx={{ color: "error.main" }}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            enableColumnFilters
            enableGlobalFilter
            enableSorting
            enablePagination
            muiTablePaginationProps={{
              showFirstButton: true,
              showLastButton: true,
            }}
            muiTablePaperProps={{
              elevation: 0,
              sx: {
                borderRadius: 0,
                border: "none",
                boxShadow: "none"
              },
            }}
            muiTableHeadCellProps={{
              sx: {
                backgroundColor: "#F9FAFB",
                fontWeight: 600,
              },
            }}
            muiTableBodyProps={{
              sx: {
                '& tr:hover': {
                  backgroundColor: '#F9FAFB',
                },
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default OutboundCalls;
